import { DownloadIcon } from "@/Icons/Download";
import { useTerminalsState } from "@/States/Terminals";
import { trpc } from "@/Utils/trpc";
import { useNavigate } from "@/Utils/useNavigate";
import { defaultTimeFilter } from "@/components/v2/DatePicker/constants";
import { ViewWithLoader } from "@/components/v2/ViewWithLoader";
import { useTrans } from "@/translations";

import { useCallback, useContext, useMemo, useState } from "react";

import { PortfolioPageContext } from "../../context";
import { MapView } from "./components/mapView";
import { TableView } from "./components/tableView";
import { ViewModeSelect } from "./components/viewModeSelect";
import {
  BuildingColumnKey,
  type BuildingListRow,
  SortDir,
  ViewMode,
} from "./types";
import { exportExcel, getBuildingListRows } from "./utils";

export const BuildingsListPortfolioPage: React.FC = () => {
  const { t } = useTrans();
  const { allTerminals, setTerminalState } = useTerminalsState();
  const navigate = useNavigate();
  const { timeFilter, selectedBuildingIds } = useContext(PortfolioPageContext);

  const [viewMode, setViewMode] = useState<ViewMode>(ViewMode.TABLE);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState<BuildingColumnKey>(
    BuildingColumnKey.RATE,
  );
  const [sortDir, setSortDir] = useState<SortDir>(SortDir.DESC);

  const { data: terminalsData, isLoading } = trpc.dwh.terminals.get.useQuery(
    timeFilter || defaultTimeFilter,
    {
      queryKeyHashFn: () =>
        [
          "terminals",
          timeFilter?.startTimeISO,
          timeFilter?.endTimeISO,
        ].toString(),
      enabled: !!timeFilter,
    },
  );

  const rows: BuildingListRow[] = useMemo(
    () =>
      getBuildingListRows({
        terminalsData,
        selectedBuildingIds,
        allTerminals,
      }),
    [terminalsData, selectedBuildingIds, allTerminals],
  );

  const searchValue = useMemo(() => search.trim().toLowerCase(), [search]);

  const filteredRows = useMemo(
    () =>
      rows.filter(
        (row) => !searchValue || row.name.toLowerCase().includes(searchValue),
      ),
    [rows, searchValue],
  );

  const sortedRows = useMemo(() => {
    const dir = sortDir === SortDir.ASC ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      if (
        sortKey === BuildingColumnKey.NAME ||
        sortKey === BuildingColumnKey.STATUS
      ) {
        return a[sortKey].localeCompare(b[sortKey]) * dir;
      }
      return (a[sortKey] - b[sortKey]) * dir;
    });
  }, [filteredRows, sortKey, sortDir]);

  const onSort = useCallback(
    (key: BuildingColumnKey) => {
      if (key === sortKey) {
        setSortDir(sortDir === SortDir.ASC ? SortDir.DESC : SortDir.ASC);
      } else {
        setSortKey(key);
        setSortDir(SortDir.DESC);
      }
    },
    [sortDir, sortKey],
  );

  const exportExcelFile = useCallback(() => {
    exportExcel({
      timeframe: timeFilter || defaultTimeFilter,
      rows: sortedRows,
    });
  }, [timeFilter, sortedRows]);

  const onSelectRow = useCallback(
    (row: BuildingListRow) => {
      const terminal = allTerminals.find((t) => t.id === row.terminalId);
      if (!terminal) return;

      setTerminalState({ currentTerminal: terminal });
      navigate("/");
    },
    [allTerminals, setTerminalState, navigate],
  );

  return (
    <div className="border-grey2 rounded-xl border bg-white px-[24px] py-[22px]">
      <div className="mb-[4px] flex flex-wrap items-center justify-between gap-3">
        <h2 className="font-dm-sans m-0 text-[16px] font-bold">
          {t("portfolioPage:buildingListTitle")}
        </h2>

        {!isLoading && (
          <div className="flex flex-wrap items-center gap-[12px]">
            <ViewModeSelect viewMode={viewMode} setViewMode={setViewMode} />

            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("portfolioPage:buildingListSearchPlaceholder")}
              className="border-grey3 w-[200px] rounded-lg border px-[12px] py-2 text-[13px] outline-none"
            />

            <button
              onClick={exportExcelFile}
              className="border-grey3 inline-flex cursor-pointer items-center gap-2 rounded-lg border bg-white px-[13px] py-2 text-[13px] font-medium text-black"
            >
              <DownloadIcon />
              {t("portfolioPage:buildingListExportButton")}
            </button>
          </div>
        )}
      </div>

      <ViewWithLoader isLoading={isLoading}>
        <div className="mt-[13px]">
          {viewMode === "table" ? (
            <TableView
              sortKey={sortKey}
              sortDir={sortDir}
              onSort={onSort}
              rows={sortedRows}
              onSelectRow={onSelectRow}
            />
          ) : (
            <MapView rows={sortedRows} />
          )}
        </div>
      </ViewWithLoader>
    </div>
  );
};
